import React from 'react';

import { useDictionary, usePage } from '@/src/app/hooks';
import { MyLessons_SelectedIcon, MyLessons_UnselectedIcon, type UnitDTO } from '@/src/ui';
import { getThumbnailLink, convertToCdnUrl } from '@/src/utils';
import lessonVideoThumbnail from '@/src/assets/img/thumbnails/video-thumbnail-lesson.png';
import strategyVideoThumbnail from '@/src/assets/img/thumbnails/video-thumbnail-strategy.png';

interface SingleLessonProps {
    unit: UnitDTO;
    grade: string;
    currentUnit: number;
    selectedLesson: number;
    myLessons: string[];
    addMyLesson: (lessonId: string) => void;
    removeMyLesson: (lessonId: string) => void;
}

const SingleLesson: React.FC<SingleLessonProps> = ({
    unit,
    grade,
    currentUnit,
    selectedLesson,
    myLessons,
    addMyLesson,
    removeMyLesson,
}) => {
    const { translate } = useDictionary();
    const { pageLanguage } = usePage();

    const lessonData = unit.lessons[selectedLesson];
    if (!lessonData) {
        return null;
    }

    const lessonLink =
        lessonData.subitems.find((item) => item.type.toLowerCase().includes('lesson'))?.link || '';
    const strategyLink =
        lessonData.subitems.find((item) => item.type.toLowerCase().includes('strategy'))?.link ||
        '';
    const isImageHorizontal = ['TK', 'K'].includes(grade);

    const lessonId = `G${grade}_U${currentUnit}_L${selectedLesson}`;
    const isSaved = myLessons?.includes(lessonId) || false;
    const SvgIcon = isSaved ? MyLessons_SelectedIcon : MyLessons_UnselectedIcon;
    const onClick = isSaved ? () => removeMyLesson(lessonId) : () => addMyLesson(lessonId);
    return (
        <React.Fragment key={`allLessons-${grade}-U${currentUnit}-L${selectedLesson}`}>
            <h3 className="lesson-tab">
                <span>
                    Lesson {selectedLesson}: {lessonData.title}
                </span>
                <a
                    id={`G${grade}lesson${selectedLesson}myLessonsButton`}
                    className="myLessonsButton"
                    onClick={onClick}
                >
                    <SvgIcon color={`g${grade}`} />
                </a>
            </h3>
            <div className="lesson-resources container-fluid">
                <div className="pdf-row pdf-grid">
                    {lessonData.subitems.map((item) => {
                        const itemType = item.type.toLowerCase();
                        if (itemType.includes('lesson') || itemType.includes('strategy')) {
                            return null;
                        }
                        return (
                            <div
                                key={`single-lesson-${selectedLesson}-resource-${item.id}`}
                                className="pdf-grid-cell mb-4"
                            >
                                <div
                                    className={`pdf-item${isImageHorizontal ? '-horizontal' : ''}`}
                                >
                                    <p className="pdf-type">{item.text}</p>
                                    <img src={getThumbnailLink(item.link)} alt={item.text} />
                                    <a
                                        href={item.link}
                                        target="_blank"
                                        className={`pdf-button pd-G${grade}-primary background`}
                                    >
                                        {translate('View PDF')}
                                    </a>
                                </div>
                            </div>
                        );
                    })}
                </div>
                <div className="row mt-4">
                    <div className="video-section-column col-12">
                        <div className="video-section">
                            {lessonLink ? (
                                <div className="video-container">
                                    <img
                                        src={lessonVideoThumbnail}
                                        className="video-thumbnail-overlay"
                                        alt="Lesson Video Image"
                                    />
                                    <video
                                        className="preview-video"
                                        muted
                                        playsInline
                                        preload="metadata"
                                        onMouseOver={(e) => e.currentTarget.play()}
                                        onMouseOut={(e) => {
                                            e.currentTarget.pause();
                                            e.currentTarget.currentTime = 0;
                                        }}
                                        onClick={() => {
                                            if (pageLanguage === 'ES') {
                                                window.open(`${lessonLink}_ES`, '_blank');
                                            } else {
                                                window.open(`${lessonLink}`, '_blank');
                                            }
                                        }}
                                    >
                                        <source
                                            src={`${convertToCdnUrl(lessonLink, pageLanguage)}`}
                                            type="video/mp4"
                                        />
                                        Your browser does not support the video tag.
                                    </video>
                                </div>
                            ) : null}

                            {strategyLink ? (
                                <div className="video-container">
                                    <img
                                        src={strategyVideoThumbnail}
                                        className="video-thumbnail-overlay"
                                        alt="Strategy Video Image"
                                    />
                                    <video
                                        className="preview-video"
                                        muted
                                        playsInline
                                        preload="metadata"
                                        onMouseOver={(e) => e.currentTarget.play()}
                                        onMouseOut={(e) => {
                                            e.currentTarget.pause();
                                            e.currentTarget.currentTime = 0;
                                        }}
                                        onClick={() => {
                                            if (pageLanguage === 'ES') {
                                                window.open(`${strategyLink}_ES`, '_blank');
                                            } else {
                                                window.open(`${strategyLink}`, '_blank');
                                            }
                                        }}
                                    >
                                        <source
                                            src={`${convertToCdnUrl(strategyLink, pageLanguage)}`}
                                            type="video/mp4"
                                        />
                                        Your browser does not support the video tag.
                                    </video>
                                </div>
                            ) : null}
                        </div>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default SingleLesson;
